import {
  AccountStates,
  AffiliationRelations,
  AffiliationTypes,
  ApplyForGovFunds,
} from '@generated/graphql';
import type { DraftAccount } from '@interfaces/account';
import type { Affiliation } from '@interfaces/affiliation';
import {
  JointAccountHolderRelations,
  RespFamilyPrimaryBeneficiaryRelations,
} from '@interfaces/affiliationRelations';
import type { Goal } from '@interfaces/goalSubAccountTypes';
import { FormGroup } from '@mui/material';
import { AccountTypes } from '@onevesthq/ov-enums';
import { CheckboxField } from '@ovComponents/2-component/checkboxField/checkboxField';
import { ClientSelectField } from '@ovComponents/2-component/clientSelectField/clientSelect';
import { Form } from '@ovComponents/2-component/form/form';
import { HelpText } from '@ovComponents/2-component/helpText/helpText';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { BeneficiaryTable } from '@ovComponents/3-pattern/beneficiaryCrud/beneficiaryTable';
import {
  GoalLinkingInput,
  getValidGoals,
} from '@ovComponents/4-module/workflowCompletion/subSteps/account/goalLinking';
import { upsertAffiliation } from '@ovComponents/4-module/workflowCompletion/subSteps/account/utils';
import {
  type FieldOptions,
  type FormErrors,
  validateFields,
} from '@ovComponents/4-module/workflowCompletion/subSteps/utils';
import { colors } from '@ovComponents/legacyColor';
import { Button, Divider, Grid, IconButton, Typography } from 'hephaestus/web';
import {
  AddRounded as AddRoundedIcon,
  CloseRounded as CloseRoundedIcon,
} from 'hephaestus/webIcons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type RespFamilyDialogContentInterface = {
  userData: any;
  showGoalLinkingSection: boolean;
  onAccountCreated: (account: DraftAccount) => void;
  onDialogClose: () => void;
};

const initAffiliation: Partial<Affiliation> = {
  allocation: 0,
  relation: AffiliationRelations.Child,
  type: AffiliationTypes.PrimaryBeneficiary,
  user: undefined,
};

export const JointRespFamilyDialogContent = ({
  userData,
  showGoalLinkingSection,
  onAccountCreated,
  onDialogClose,
}: RespFamilyDialogContentInterface) => {
  const { t } = useTranslation([
    'affiliationTypes',
    'components',
    'workflowCompletions',
  ]);
  const [yes, no] = [
    t('components:booleanOptions.yes'),
    t('components:booleanOptions.no'),
  ];

  const [errors, setErrors] = useState<FormErrors>(null);
  const [focused, setFocused] = useState<string[]>([]);
  const [applyForGovFunds, setApplyForGovFunds] = useState<ApplyForGovFunds[]>(
    [],
  );
  const [affiliations, setAffiliations] = useState<Affiliation[]>([]);
  const [affiliation, setAffiliation] =
    useState<Partial<Affiliation>>(initAffiliation);
  const [jointHolder, setJointHolder] = useState<Partial<Affiliation>>({
    ...initAffiliation,
    type: AffiliationTypes.Joint,
    relation: AffiliationRelations.CommonLaw,
  });
  const [goalsLinked, setGoalsLinked] = useState<Goal[]>([]);
  const goals = getValidGoals(userData);
  const clearPreviousAffiliation = () =>
    setAffiliation({ ...initAffiliation, user: affiliation.user });
  const clearFocused = (fieldsToClear?: string[]) =>
    setFocused((prevState) =>
      fieldsToClear === undefined
        ? []
        : prevState.filter((field) => !fieldsToClear?.includes(field)),
    );
  const handleApplyForGovFundSelect = (
    govFund: ApplyForGovFunds,
    checked: boolean,
  ) => {
    setFocused([...focused, 'applyForGovFunds']);
    if (checked) {
      setApplyForGovFunds((prevState) => [...prevState, govFund]);
    } else {
      setApplyForGovFunds([
        ...applyForGovFunds.filter((item) => item !== govFund),
      ]);
    }
  };
  const isNotSelfRelation = useCallback(
    (aff: Affiliation) => userData.id && aff?.user?.id !== userData?.id,
    [userData.id],
  );
  const hasBeneficiary = !!affiliations.find(isNotSelfRelation);
  const userIsARelation = (relation: AffiliationRelations) =>
    !!affiliations.find(
      (item) => item.relation === relation && item.user?.id === userData.id,
    );
  const handleUserAsGuardianOrCareGiver = (
    relation: AffiliationRelations,
    checked: boolean,
  ) => {
    if (checked) {
      const userAffiliation = {
        relation,
        type: AffiliationTypes.Other,
        user: userData,
      };
      setAffiliations((prevState) => [...prevState, userAffiliation]);
    } else {
      setAffiliations([
        ...affiliations.filter(
          (item) => item.user?.id !== userData.id || item.relation !== relation,
        ),
      ]);
    }
  };

  const fieldOptions: FieldOptions = useMemo(
    () => ({
      applyForGovFunds: { required: true },
      primaryBeneficiary: { required: true },
      affiliationUser: { required: true },
      affiliationType: { required: true },
      affiliationRelation: { required: true },
      jointHolderUser: { required: true },
      jointHolderRelation: { required: true },
    }),
    [],
  );

  const validate = useCallback(
    (candidateFields?: string[]): FormErrors => {
      const data = {
        applyForGovFunds,
        primaryBeneficiary: affiliations.filter(isNotSelfRelation),
        affiliationUser: hasBeneficiary || affiliation.user,
        affiliationType: hasBeneficiary || affiliation.type,
        affiliationRelation: hasBeneficiary || affiliation.relation,
        jointHolderUser: jointHolder.user,
        jointHolderRelation: jointHolder.relation,
      };
      const newErrors = validateFields(fieldOptions, data, candidateFields);
      setErrors(newErrors);
      return newErrors;
    },
    [
      affiliation,
      affiliations,
      applyForGovFunds,
      fieldOptions,
      hasBeneficiary,
      isNotSelfRelation,
      jointHolder,
    ],
  );

  const submit = () => {
    const formErrors = validate();
    if (formErrors) {
      setFocused(Object.keys(formErrors));
      return;
    }
    onAccountCreated({
      type: AccountTypes.RESP_FAMILY_JOINT,
      applyForGovFunds,
      id: '',
      state: AccountStates.Active,
      user: userData,
      affiliations: [...affiliations, jointHolder as Affiliation],
      goalsLinked,
    });
  };
  const addAffiliation = (): void => {
    const fieldsToCheck = [
      'affiliationUser',
      'affiliationType',
      'affiliationRelation',
    ];

    const formErrors = validate(fieldsToCheck);
    if (formErrors) {
      setFocused([...focused, ...Object.keys(formErrors)]);
      return;
    }
    setAffiliations((prevState) =>
      upsertAffiliation(affiliation as Affiliation, prevState),
    );
    clearPreviousAffiliation();
    clearFocused(fieldsToCheck);
  };

  useEffect(() => {
    validate(focused);
  }, [validate, focused]);

  return (
    <Form onSubmit={submit}>
      <Grid
        container
        sx={{
          gap: 3,
        }}
      >
        <Grid container>
          <Grid
            size={12}
            sx={{
              display: 'flex',
              justifyContent: 'end',
            }}
          >
            <IconButton
              onClick={onDialogClose}
              aria-label='close'
              size='medium'
            >
              <CloseRoundedIcon fontSize='inherit' />
            </IconButton>
          </Grid>
          <Grid size={12}>
            <Typography variant='headingMedium'>
              {t(
                'workflowCompletions:respAccountCreation.jointRespFamilyTitle',
              )}
            </Typography>
          </Grid>
        </Grid>
        <Grid size={12}>
          <Typography variant='bodyLarge'>
            {t('workflowCompletions:respAccountCreation.subtitle')}
          </Typography>
        </Grid>
        <Grid
          container
          sx={{
            gap: 1,
          }}
        >
          <Grid size={12}>
            <FormGroup row sx={{ '&>*': { mr: 3 } }}>
              <CheckboxField
                label={t(
                  'workflowCompletions:respAccountCreation.basicCanadaEducationSavingsGrant',
                )}
                checked={applyForGovFunds.includes(ApplyForGovFunds.BasicCesg)}
                onChange={(checked) =>
                  handleApplyForGovFundSelect(
                    ApplyForGovFunds.BasicCesg,
                    checked,
                  )
                }
              />

              <CheckboxField
                label={t(
                  'workflowCompletions:respAccountCreation.additionalCESG',
                )}
                checked={applyForGovFunds.includes(
                  ApplyForGovFunds.AdditionalCesg,
                )}
                onChange={(checked) =>
                  handleApplyForGovFundSelect(
                    ApplyForGovFunds.AdditionalCesg,
                    checked,
                  )
                }
              />

              <CheckboxField
                label={t(
                  'workflowCompletions:respAccountCreation.canadaLearningBond',
                )}
                checked={applyForGovFunds.includes(ApplyForGovFunds.Clb)}
                onChange={(checked) =>
                  handleApplyForGovFundSelect(ApplyForGovFunds.Clb, checked)
                }
              />
            </FormGroup>
          </Grid>
          {errors?.applyForGovFunds && (
            <Grid size={12}>
              <HelpText tone='error' text={errors?.applyForGovFunds?.message} />
            </Grid>
          )}
          <Grid size={12}>
            <Divider variant='fullWidth' />
          </Grid>
        </Grid>
        <Grid
          container
          size={12}
          sx={{
            gap: 1,
          }}
        >
          <Grid size={12}>
            <Typography variant='headingXSmall'>
              {t('workflowCompletions:jointAccountHolder.label')}
            </Typography>
          </Grid>
          <Typography
            variant='monospacedLarge'
            sx={{
              color:
                errors?.jointHolderUser || errors?.jointHolderRelation
                  ? colors.error
                  : colors.neutral600,
            }}
          >
            {t('workflowCompletions:jointAccountHolder.subLabel')}
          </Typography>
        </Grid>
        <Grid
          container
          spacing={1}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Grid size={6}>
            <ClientSelectField
              label={t('workflowCompletions:respAccountCreation.name')}
              user={jointHolder.user as any}
              setUser={(item) =>
                setJointHolder((prevState) => ({ ...prevState, user: item }))
              }
              onBlur={() => setFocused([...focused, 'jointHolderUser'])}
              error={!!errors?.jointHolderUser}
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <SelectField
              onChange={(e: any) =>
                setJointHolder((prevState) => ({
                  ...prevState,
                  relation: e.target.value,
                }))
              }
              label={t('workflowCompletions:relationship')}
              fullWidth
              onBlur={() => setFocused([...focused, 'jointHolderRelation'])}
              value={jointHolder.relation}
              error={!!errors?.jointHolderRelation}
            >
              {JointAccountHolderRelations.map(
                (value: string, index: number) => (
                  <MenuItem value={value} key={index}>
                    {t(`affiliationTypes:affiliationRelationship.${value}`)}
                  </MenuItem>
                ),
              )}
            </SelectField>
          </Grid>
        </Grid>
        <Grid size={12}>
          <Divider variant='fullWidth' />
        </Grid>
        <Grid
          container
          size={12}
          sx={{
            gap: 1,
          }}
        >
          <Grid size={12}>
            <Typography variant='headingXSmall'>
              {t('workflowCompletions:respAccountCreation.addAnAffiliate')}
            </Typography>
          </Grid>
          {hasBeneficiary ? (
            <BeneficiaryTable
              hideAllocation
              beneficiaries={affiliations}
              setBeneficiaries={(items) => setAffiliations([...items])}
            />
          ) : (
            <Typography
              variant='monospacedLarge'
              sx={{
                color: errors?.primaryBeneficiary
                  ? colors.error
                  : colors.neutral600,
              }}
            >
              {t(
                'workflowCompletions:respAccountCreation.respAccountsMustHaveAtLeastOneAffiliate',
              )}
            </Typography>
          )}
        </Grid>
        <Grid
          container
          size={12}
          sx={{
            gap: 3,
          }}
        >
          <Grid size={12}>
            <ClientSelectField
              label={t('workflowCompletions:respAccountCreation.name')}
              user={affiliation.user as any}
              setUser={(item) =>
                setAffiliation((prevState) => ({ ...prevState, user: item }))
              }
              onBlur={() => setFocused([...focused, 'affiliationUser'])}
              fullWidth
              error={!!errors?.affiliationUser}
            />
          </Grid>
          <Grid size={12}>
            <SelectField
              onChange={(e: any) =>
                setAffiliation((prevState) => ({
                  ...prevState,
                  type: e.target.value,
                }))
              }
              onBlur={() => setFocused([...focused, 'affiliationType'])}
              label={t(
                'workflowCompletions:respAccountCreation.affiliationType',
              )}
              fullWidth
              value={affiliation.type}
              error={!!errors?.affiliationType}
            >
              <MenuItem value='PRIMARY_BENEFICIARY'>
                {t('affiliationTypes:PRIMARY_BENEFICIARY')}
              </MenuItem>
            </SelectField>
          </Grid>
          <Grid size={12}>
            <SelectField
              onChange={(e: any) =>
                setAffiliation((prevState) => ({
                  ...prevState,
                  relation: e.target.value,
                }))
              }
              label={t(
                'workflowCompletions:respAccountCreation.relationshipToAccountHolder',
              )}
              fullWidth
              value={affiliation.relation}
              onBlur={() => setFocused([...focused, 'affiliationRelation'])}
              error={!!errors?.affiliationRelation}
            >
              {RespFamilyPrimaryBeneficiaryRelations.map((value, index) => (
                <MenuItem value={value} key={index}>
                  {t(`affiliationTypes:affiliationRelationship.${value}`)}
                </MenuItem>
              ))}
            </SelectField>
          </Grid>
          <Grid size={12}>
            <Button
              variant='outlined'
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                setFocused([...focused, 'primaryBeneficiary']);
                addAffiliation();
              }}
              type='button'
            >
              {t('workflowCompletions:baseAccountCreation.addAffiliate')}
            </Button>
          </Grid>
        </Grid>
        <Grid size={12}>
          <Divider variant='fullWidth' />
        </Grid>
        <Grid container size={12}>
          <Grid size={12}>
            <Typography variant='button' sx={{ color: colors.neutral600 }}>
              {t('workflowCompletions:respAccountCreation.isLegalGuardian')}
            </Typography>
          </Grid>
          <Grid size={12}>
            <RadioGroup
              onChange={(e: any) =>
                handleUserAsGuardianOrCareGiver(
                  AffiliationRelations.Guardian,
                  e.target.value === 'Yes',
                )
              }
              label=''
              fullWidth
              value={
                userIsARelation(AffiliationRelations.Guardian) ? 'Yes' : 'No'
              }
            >
              <Radio
                value='Yes'
                size='small'
                disabled={!hasBeneficiary}
                label={yes}
              />

              <Radio
                value='No'
                size='small'
                disabled={!hasBeneficiary}
                label={no}
              />
            </RadioGroup>
          </Grid>
        </Grid>
        <Grid container size={12}>
          <Grid size={12}>
            <Typography variant='button' sx={{ color: colors.neutral600 }}>
              {t('workflowCompletions:respAccountCreation.isCareGiver')}
            </Typography>
          </Grid>
          <Grid size={12}>
            <RadioGroup
              onChange={(e: any) =>
                handleUserAsGuardianOrCareGiver(
                  AffiliationRelations.PrimaryCaregiver,
                  e.target.value === 'Yes',
                )
              }
              label=''
              fullWidth
              value={
                userIsARelation(AffiliationRelations.PrimaryCaregiver)
                  ? 'Yes'
                  : 'No'
              }
            >
              <Radio
                value='Yes'
                size='small'
                disabled={!hasBeneficiary}
                label={yes}
              />

              <Radio
                value='No'
                size='small'
                disabled={!hasBeneficiary}
                label={no}
              />
            </RadioGroup>
          </Grid>
        </Grid>
        <Grid size={12}>
          <Divider variant='fullWidth' />
        </Grid>
        <Grid size={12}>
          <Divider variant='fullWidth' />
        </Grid>
        {showGoalLinkingSection && (
          <Grid size={12}>
            <GoalLinkingInput
              goals={goals}
              goalsLinked={goalsLinked}
              setGoalsLinked={setGoalsLinked}
            />
          </Grid>
        )}
        <Grid
          size={12}
          sx={{
            display: 'flex',
            justifyContent: 'end',
          }}
        >
          <Button color='primary' type='submit'>
            {t('workflowCompletions:respAccountCreation.addAccount')}
          </Button>
        </Grid>
      </Grid>
    </Form>
  );
};

export default JointRespFamilyDialogContent;
